import { removeChildren } from "@rootreeweb/js-lib";

import { render } from "./singleResult.js";
import type { IResponseData } from "./types.js";

type Result = {
	body: string;
	count: string;
	title: string;
};
const zeroResult: Result = {
	body: "<div class='no-results-found rt-search-popup-no-results'>No results found</div>",
	count: ``,
	title: `Search Results`,
};

const elements = {
	body: document.querySelector("#searchcomplete-resultslist"),
	count: document.querySelector("#header-search-result-info"),
	title: document.querySelector("#rt-search-result-title"),
};
/**
 * Renders the search results returned from AJAX.
 *
 * @param response The response from the AJAX request.
 * @param lastQuery The last query that was searched.
 * @throws {Error} If the search results list element is not found.
 */
export function processResults(
	response: IResponseData,
	lastQuery: string,
): void {
	if (!isLastQuery(response.query, lastQuery)) return;
	if (!elements.body) {
		throw new Error("No search results list element found");
	}
	removeChildren(elements.body);
	const result = response.count
		? processContentfulResult(response)
		: zeroResult;
	if (result) {
		outputResults(result);
	}
}

/**
 * Checks if the query is the last query searched. Used to prevent rendering old
 * queries.
 *
 * @param query The current query.
 * @param lastQuery The last query searched.
 * @returns True if the query is the last query searched, false otherwise.
 */
function isLastQuery(query: string, lastQuery: string): boolean {
	if (!query) return true;

	const cleanedQuery = query.replace("&amp;", "&");
	return cleanedQuery === lastQuery;
}

/**
 * Creates the meta-information for the search results.
 *
 * @param response The response from the AJAX request.
 * @returns The meta-information for the search results.
 */
function meta(response: IResponseData): Result {
	if (typeof response.count === "number") {
		if (response.count > 99) {
			return {
				body: ``,
				count: `<strong>99+</strong> results for <strong>${response.query}</strong>`,
				title: `Search Results`,
			};
		} else {
			return {
				body: ``,
				count: `<strong>${response.count}</strong> results for <strong>${response.query}</strong>`,
				title: `Search Results`,
			};
		}
	} else {
		return {
			body: ``,
			count: ``,
			title: `Top Pages`,
		};
	}
}

/**
 * Outputs the search results to the DOM.
 *
 * @param results The results to output.
 */
function outputResults(results: Result): void {
	if (elements.title) elements.title.textContent = results.title;
	if (elements.body) elements.body.innerHTML = results.body;
	if (elements.count) elements.count.innerHTML = results.count;
}

/**
 * Processes the results from a query that returned results.
 *
 * @param response The response from the AJAX request.
 * @returns The results to display.
 */
function processContentfulResult(response: IResponseData): null | Result {
	const { results: returnedResults } = response;

	let resultHtml = ``;

	returnedResults.forEach((element) => {
		resultHtml += render(element);
	});

	const results = meta(response);
	results.body = resultHtml;

	return results;
}
